import React, { useState, useEffect } from 'react';
import moment from 'moment';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField,
  Stepper, Step, StepLabel, IconButton, Typography, CircularProgress, Chip, Tooltip,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import PlacesAutocomplete from 'react-places-autocomplete';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import DescriptionIcon from '@material-ui/icons/Description';
import { WIZARD_STEPS, emptyPlan, REPORT_STATUS_OPTIONS, PROJECT_TYPE_OPTIONS } from '../constants';
import { backendDateFormat, fieldDateFormat } from '../../../../constants';
import { checkApiStatus } from '../../../../utils_v2';
import { globalGetService, globalPostService, globalPutService } from '../../../../utils_v2/globalApiServices';
import { buildPlanPayload } from '../utils';
import { trackActivity } from '../../../../utils/mixpanel';
import {
  createProgramPlanApi, updateProgramPlanApi, getPlannerAssetsApi,
  getTeamMembersApi, uploadRepCvApi, getStageTemplateStagesApi,
} from '../apiService';

const errorMessage = (response) => (response && response.data && response.data.message) || 'Unable to process the request due to a technical error.';

const PDF_VIEWER_BASE = 'https://sparta-aims.ams3.digitaloceanspaces.com/others/tools/pdf.js/web/viewer.html?file=';
const OFFICE_VIEWER_BASE = 'https://view.officeapps.live.com/op/embed.aspx?src=';
const OFFICE_EXTENSIONS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv'];
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'];

const getFileExtension = (name = '') => (name.split('.').pop() || '').toLowerCase();

const CvPreviewDialog = ({ file, onClose }) => {
  const extension = getFileExtension(file.name);
  const encodedUrl = encodeURIComponent(file.url);
  return (
    <Dialog open maxWidth="md" fullWidth>
      <DialogTitle>{file.name}</DialogTitle>
      <DialogContent dividers style={{ padding: 0, height: '70vh' }}>
        {extension === 'pdf' ? (
          <object data={`${PDF_VIEWER_BASE}${encodedUrl}`} type="text/html" width="100%" height="100%">
            <Typography variant="body2" style={{ padding: 16 }}>Unable to preview this file.</Typography>
          </object>
        ) : IMAGE_EXTENSIONS.includes(extension) ? (
          <img src={file.url} alt={file.name} style={{ maxWidth: '100%', maxHeight: '100%', display: 'block', margin: '0 auto' }} />
        ) : OFFICE_EXTENSIONS.includes(extension) ? (
          <iframe title={file.name} src={`${OFFICE_VIEWER_BASE}${encodedUrl}`} width="100%" height="100%" style={{ border: 'none' }} />
        ) : (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Typography variant="body2" style={{ marginBottom: 12 }}>Preview isn't available for this file type.</Typography>
            <Button variant="outlined" color="primary" href={file.url} target="_blank" rel="noopener noreferrer">Open File</Button>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const OptionField = ({ label, options, value, error, onChange, onFocus, required = true, disabled = false }) => (
  <Autocomplete
    options={options}
    disabled={disabled}
    getOptionLabel={(option) => (typeof option === 'string' ? option : (option?.name || option?.label))}
    getOptionSelected={(option, current) => (typeof option === 'string' ? option === current : option.id === current.id)}
    value={value || null}
    onChange={(event, newValue) => onChange(newValue || null)}
    renderInput={(params) => (
      <TextField
        {...params}
        required={required}
        label={label}
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
        error={!!error}
        helperText={error}
        onFocus={onFocus}
      />
    )}
  />
);

const LocationField = ({ location, error, onFieldChange, onFieldFocus }) => {
  const [address, setAddress] = useState(location || "");
  useEffect(() => {
    setAddress(location || "");
  }, [location]);
  const handleSelect = (selectedAddress) => {
    setAddress(selectedAddress);
    onFieldChange('location', selectedAddress);
  };
  return (
    <div style={{ position: 'relative' }}>
      <PlacesAutocomplete
        value={address}
        onChange={setAddress}
        onSelect={handleSelect}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div>
            <TextField
              fullWidth required label="Location" variant="outlined" size="small"
              InputLabelProps={{ shrink: true }}
              error={!!error}
              helperText={error || ''}
              {...getInputProps({
                placeholder: 'Search Location ...',
                className: 'location-search-input',
                onFocus: onFieldFocus,
              })}
            />
            <div className="autocomplete-dropdown-container" style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: '#fff', maxHeight: 200, overflowY: 'auto' }}>
              {loading && <div>Loading...</div>}
              {suggestions.map((suggestion) => {
                const className = suggestion.active ? 'suggestion-item--active' : 'suggestion-item';
                const style = suggestion.active
                  ? { backgroundColor: '#fafafa', cursor: 'pointer', padding: 10 }
                  : { backgroundColor: '#ffffff', cursor: 'pointer', padding: 10 };
                return (
                  <div {...getSuggestionItemProps(suggestion, { className, style })}>
                    <span>{suggestion.description}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </PlacesAutocomplete>
    </div>
  );
};

const BasicInfoStep = ({ formData, errors, onFieldChange, onFieldFocus, lookups }) => (
  <Grid container spacing={2}>
    <Grid item xs={12}>
      <TextField
        fullWidth required label="Project Name" variant="outlined" size="small"
        InputLabelProps={{ shrink: true }}
        inputProps={{ maxLength: 255 }}
        value={formData?.name || ""} error={!!errors.name} helperText={errors.name || ''}
        onChange={(event) => onFieldChange('name', event.target.value)}
        onFocus={() => onFieldFocus('name')}
      />
    </Grid>
    <Grid item xs={6}>
      <OptionField label="Project Type" options={PROJECT_TYPE_OPTIONS} value={formData?.project_type || null} error={errors.project_type} onChange={(value) => onFieldChange('project_type', value)} onFocus={() => onFieldFocus('project_type')} />
    </Grid>
    {/* <Grid item xs={6}>
      <OptionField label="Inspection Type" options={lookups?.inspectionTypes || []} value={formData?.inspection_type || null} error={errors?.inspection_type} onChange={(value) => onFieldChange('inspection_type', value)} onFocus={() => onFieldFocus('inspection_type')} />
    </Grid> */}
    <Grid item xs={6}>
      <OptionField label="Lessor" options={lookups?.lessors || [] } value={formData?.lessor || null} error={errors?.lessor} onChange={(value) => onFieldChange('lessor', value)} onFocus={() => onFieldFocus('lessor')} />
    </Grid>
    <Grid item xs={6}>
      <OptionField label="Lessee" options={lookups?.lessees || [] } value={formData?.lessee || null} error={errors?.lessee} onChange={(value) => onFieldChange('lessee', value)} onFocus={() => onFieldFocus('lessee')} />
    </Grid>
    <Grid item xs={6}>
      <OptionField label="Client" options={[{ id: 1, label: 'Lessor' }, { id: 2, label: 'Lessee' }]} value={formData?.client || null} error={errors?.client} onChange={(value) => onFieldChange('client', value)} onFocus={() => onFieldFocus('client')} />
    </Grid>
    <Grid item xs={12}>
      <LocationField
        location={formData?.location}
        error={errors?.location}
        onFieldChange={onFieldChange}
        onFieldFocus={() => onFieldFocus('location')}
      />
    </Grid>
    <Grid item xs={12}>
      <OptionField
        label="Stage Template" options={lookups?.stageTemplates || [] } value={formData?.stage_template || null}
        error={errors?.stage_template}
        onChange={(value) => onFieldChange('stage_template', value)}
        onFocus={() => onFieldFocus('stage_template')}
        disabled={!!formData.slug}
      />
      {formData?.slug ? (
        <Typography variant="caption" style={{ color: '#9ca3af' }}>Stage template can't be changed after creation</Typography>
      ) : null}
    </Grid>
  </Grid>
);

const AssetsStep = ({ formData, errors, onFieldChange, enqueueSnackbar }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const lessorId = formData?.lessor && formData?.lessor?.id;

  useEffect(() => {
    if (!lessorId) { setOptions([]); setFetchError(''); return; }
    setLoading(true);
    setFetchError('');
    globalGetService(getPlannerAssetsApi(), { lessor_id: lessorId, status: 'available' })
      .then((response) => {
        setLoading(false);
        if (checkApiStatus(response)) {
          const payload = response?.data?.data || {};
          const list = payload?.list || payload?.data || [];
          setOptions((list || []).map((asset) => ({
            name: `${asset?.asset_type?.label || ''} - ${asset?.identifier}`,
            asset_type: asset?.asset_type?.id,
            asset_id: asset?.asset_id,
            identifier: asset?.identifier,
          })));
        } else {
          setFetchError(errorMessage(response));
          setOptions([]);
          enqueueSnackbar(errorMessage(response), { variant: 'error' });
        }
      })
  }, [lessorId]);

  const assetLimit = formData?.expected_asset_count ? Number(formData.expected_asset_count) : null;
  const selectedAssets = formData?.assets?.map((asset) => ({
    ...asset,
    name: asset.name || `${(asset?.asset_type && asset?.asset_type?.label) || ''} - ${asset.identifier}`,
  }));

  const handleAssetsChange = (newValue) => {
    const next = newValue || [];
    if (assetLimit && next.length > assetLimit) {
      enqueueSnackbar(`You can select at most ${assetLimit} asset${assetLimit === 1 ? '' : 's'} — the count entered above.`, { variant: 'error' });
      return;
    }
    onFieldChange('assets', next);
  };

  return (
    <>
      <TextField
        fullWidth label="Number of Assets" placeholder="e.g. 3" helperText="Optional — how many aircraft/engine assets this plan covers"
        variant="outlined" size="small" type="number" inputProps={{ min: 0 }}
        InputLabelProps={{ shrink: true }}
        value={formData?.expected_asset_count || ""} onChange={(event) => onFieldChange('expected_asset_count', event.target.value)}
        style={{ marginBottom: 16 }}
      />
      {!lessorId ? (
        <Typography variant="body2" style={{ padding: 10, backgroundColor: '#fef3c7', color: '#92400e', borderRadius: 4, marginBottom: 12 }}>
          Pick a Lessor on the Basic Info step first — the asset list is scoped to that lessor's fleet.
        </Typography>
      ) : null}
      <Grid container spacing={2} style={{ marginBottom: 8 }}>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={assetLimit && formData.assets.length >= assetLimit ? [] : (options || [])}
            getOptionLabel={(option) => option.name || ''}
            getOptionSelected={(option, value) => option?.asset_id === value?.asset_id}
            value={selectedAssets}
            onChange={(event, newValue) => handleAssetsChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                label={`Available assets (${formData?.assets?.length || ''}${formData?.expected_asset_count ? ` of ${formData.expected_asset_count}` : ''} added)`}
                error={!!fetchError || !!errors?.assets}
                helperText={errors?.assets || fetchError || (loading ? 'Loading assets…' : (assetLimit && formData?.assets?.length >= assetLimit ? `Asset limit of ${assetLimit} reached` : ''))}
              />
            )}
          />
        </Grid>
      </Grid>
    </>
  );
};

const TimelineStep = ({ formData, errors, onFieldChange, onFieldFocus }) => (
  <MuiPickersUtilsProvider utils={MomentUtils}>
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <KeyboardDatePicker
          fullWidth label="Start Date" format={fieldDateFormat} inputVariant="outlined" margin="dense"
          InputLabelProps={{ shrink: true }}
          value={formData?.planned_start_date || null} error={!!errors.planned_start_date} helperText={errors?.planned_start_date || ''}
          onChange={(date) => onFieldChange('planned_start_date', date ? moment(date).format(backendDateFormat) : '')}
          onFocus={() => onFieldFocus('planned_start_date')}
        />
      </Grid>
      <Grid item xs={6}>
        <KeyboardDatePicker
          fullWidth required label="End Date" format={fieldDateFormat} inputVariant="outlined" margin="dense"
          InputLabelProps={{ shrink: true }}
          value={formData?.planned_end_date || null} error={!!errors.planned_end_date} helperText={errors?.planned_end_date || ''}
          onChange={(date) => onFieldChange('planned_end_date', date ? moment(date).format(backendDateFormat) : '')}
          onFocus={() => onFieldFocus('planned_end_date')}
        />
      </Grid>
    </Grid>
  </MuiPickersUtilsProvider>
);

const TeamStep = ({ formData, onFieldChange, enqueueSnackbar }) => {
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [uploadingRowId, setUploadingRowId] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    setLoadingRoster(true);
    globalGetService(getTeamMembersApi()).then((response) => {
      setLoadingRoster(false);
      if (checkApiStatus(response)) {
        const list = response?.data?.data?.list || [];
        setRoster((list || []).map((member) => ({
          id: member?.user_id,
          name: member?.user?.name
        })));
      }
    });
  }, []);

  const addRep = (member) => {
    if (!member || formData?.reps?.some((rep) => rep?.user_id === member?.id)) return;
    const entry = { user_id: member?.id, name: member?.name, is_lead: false, is_scoped_to_plan: true };
    onFieldChange('reps', [...formData.reps, entry]);
  };

  const removeRep = (target) => onFieldChange('reps', formData.reps.filter((rep) => rep !== target));

  const toggleLead = (target) => {
    const nextLead = !target.is_lead;
    onFieldChange('reps', formData.reps.map((rep) => (
      rep === target ? { ...rep, is_lead: nextLead } : { ...rep, is_lead: nextLead ? false : rep.is_lead }
    )));
  };

  const handleCvChange = (target, event) => {
    const file = event.target.files[0];
    const targetRowId = target.rowId || target.id;
    if (!file || !formData.slug || !targetRowId) return;
    setUploadingRowId(targetRowId);
    const cvFormData = new FormData();
    cvFormData.append('file', file);
    globalPostService(uploadRepCvApi(formData.slug, targetRowId), cvFormData, true).then((response) => {
      setUploadingRowId(null);
      if (checkApiStatus(response)) {
        const cvFileName = response?.data?.data?.cv_file_name || file?.name;
        const cvFileUrl = response?.data?.data?.cv_file_url;
        onFieldChange('reps', formData.reps.map((rep) => (rep === target ? { ...rep, cvFileName, ...(cvFileUrl ? { cvFileUrl } : {}) } : rep)));
        trackActivity('Project Management', {
          event_type: 'Upload Rep CV Success',
          page_title: 'Planner Tracker Add/Update Program Plan',
          plan_slug: formData.slug || '',
          rep_row_id: targetRowId,
          file_name: file?.name || '',
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: 'Upload Rep CV Failed',
          page_title: 'Planner Tracker Add/Update Program Plan',
          plan_slug: formData.slug || '',
          rep_row_id: targetRowId,
          file_name: file?.name || '',
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  return (
    <>
      <Autocomplete
        options={roster || []}
        loading={loadingRoster}
        getOptionLabel={(option) => option?.name || ''}
        value={null}
        onChange={(event, value) => addRep(value)}
        renderInput={(params) => (
          <TextField
            {...params} label="Assign Reps" variant="outlined" size="small" InputLabelProps={{ shrink: true }}
            InputProps={{ ...params.InputProps, endAdornment: (<>{loadingRoster ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }}
          />
        )}
      />

      {formData.reps.map((rep, index) => {
        const rowId = rep?.rowId || rep?.id;
        const cvFileName = rep?.cvFileName || rep?.cv_file_name;
        const cvFileUrl = rep?.cvFileUrl || rep?.cv_file_url;
        return (
          <div key={`${rep?.user_id}-${index}`} style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eef0f4' }}>
            <div style={{ flex: 1 }}>
              <div>
                {rep?.name || (rep?.user && rep?.user?.name)}
                {rep?.is_lead ? <Chip size="small" color="primary" label="Lead" style={{ marginLeft: 6 }} /> : null}
              </div>
              {cvFileName ? (
                <div
                  style={{ fontSize: 11, color: cvFileUrl ? '#4f46e5' : '#9ca3af', display: 'flex', alignItems: 'center', marginTop: 2, cursor: cvFileUrl ? 'pointer' : 'default' }}
                  onClick={() => cvFileUrl && setPreviewFile({ url: cvFileUrl, name: cvFileName })}
                >
                  <DescriptionIcon fontSize="small" style={{ marginRight: 4 }} />{cvFileName}
                </div>
              ) : null}
            </div>
            <Button size="small" onClick={() => toggleLead(rep)}>{rep?.is_lead ? 'Unset Lead' : 'Set Lead'}</Button>
            <Tooltip title={formData?.slug ? (rowId ? 'Upload CV' : 'Save first') : 'Save the plan first to upload a CV'}>
              <span>
                <IconButton size="small" component="label" disabled={!formData.slug || !rowId || uploadingRowId === rowId}>
                  {uploadingRowId === rowId ? <CircularProgress size={16} /> : <DescriptionIcon fontSize="small" color='primary' />}
                  <input type="file" hidden onChange={(event) => handleCvChange(rep, event)} />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={() => removeRep(rep)} ><DeleteOutlineIcon fontSize="small" color='secondary' /></IconButton>
          </div>
        );
      })}

      {previewFile ? <CvPreviewDialog file={previewFile} onClose={() => setPreviewFile(null)} /> : null}
    </>
  );
};

const TrackingStep = ({ formData, onFieldChange, enqueueSnackbar }) => {
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(false);
  const templateId = formData?.stage_template && formData?.stage_template.id;

  useEffect(() => {
    if (!templateId) { setStages([]); return; }
    setLoadingStages(true);
    globalGetService(getStageTemplateStagesApi(templateId)).then((response) => {
      setLoadingStages(false);
      if (checkApiStatus(response)) {
        setStages(response?.data?.data || []);
      } else {
        setStages([]);
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
      }
    });
  }, [templateId]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <OptionField
          label="Status (Stage)" required={false} options={stages || []} disabled={loadingStages || formData?.slug}
          value={formData?.current_stage || null}
          onChange={(value) => onFieldChange('current_stage', value)}
        />
      </Grid>
      <Grid item xs={6}>
        <OptionField
          label="Report Status – Records" required={false} options={REPORT_STATUS_OPTIONS}
          value={formData?.report_status_records || null}
          onChange={(value) => onFieldChange('report_status_records', value)}
        />
      </Grid>
      <Grid item xs={6}>
        <OptionField
          label="Report Status – Physical" required={false} options={REPORT_STATUS_OPTIONS}
          value={formData?.report_status_physical || null}
          onChange={(value) => onFieldChange('report_status_physical', value)}
        />
      </Grid>
      <Grid item xs={6}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardDatePicker
            fullWidth label="Report Submission Date" format={fieldDateFormat} inputVariant="outlined" size="small"
            InputLabelProps={{ shrink: true }}
            value={formData?.report_submission_date || null}
            onChange={(date) => onFieldChange('report_submission_date', date ? moment(date).format(backendDateFormat) : '')}
          />
        </MuiPickersUtilsProvider>
      </Grid>
      <Grid item xs={6}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <KeyboardDatePicker
            fullWidth label="Client Acceptance Date" format={fieldDateFormat} inputVariant="outlined" size="small"
            InputLabelProps={{ shrink: true }}
            value={formData?.client_acceptance_date || null}
            onChange={(date) => onFieldChange('client_acceptance_date', date ? moment(date).format(backendDateFormat) : '')}
          />
        </MuiPickersUtilsProvider>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth multiline minRows={2} label="Remarks – Records" placeholder="Notes related to records collection & review…"
          variant="outlined" size="small" InputLabelProps={{ shrink: true }}
          value={formData?.remarks_records || ""} onChange={(event) => onFieldChange('remarks_records', event.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth multiline minRows={2} label="Remarks – Physical Inspection" placeholder="Notes related to physical inspection activities…"
          variant="outlined" size="small" InputLabelProps={{ shrink: true }}
          value={formData?.remarks_physical || ""} onChange={(event) => onFieldChange('remarks_physical', event.target.value)}
        />
      </Grid>
    </Grid>
  );
};

const FIELD_ERROR_MESSAGES = {
  name: 'Please enter Project Name',
  project_type: 'Please select Project Type',
  // inspection_type: 'Please select Inspection Type',
  lessor: 'Please select Lessor',
  lessee: 'Please select Lessee',
  location: 'Please select Location',
  client: 'Please select Client',
  stage_template: 'Please select Stage Template',
  planned_end_date: 'Please select End Date',
};

const STEP_VALIDATORS = {
  basic: (formData) => {
    const errors = {};
    ['name', 'project_type', 'lessor', 'lessee', 'location', 'client'].forEach((field) => {
      if (!formData[field]) errors[field] = FIELD_ERROR_MESSAGES[field];
    });
    if (!formData.slug && !formData.stage_template) errors.stage_template = FIELD_ERROR_MESSAGES.stage_template;
    return errors;
  },
  timeline: (formData) => {
    const errors = {};
    if (!formData.planned_end_date) errors.planned_end_date = FIELD_ERROR_MESSAGES.planned_end_date;
    if (formData.planned_start_date && formData.planned_end_date && !moment(formData.planned_end_date).isAfter(formData.planned_start_date)) {
      errors.planned_end_date = 'End Date should be after Start Date';
    }
    return errors;
  },
  assets: (formData) => {
    const errors = {};
    if (formData?.expected_asset_count && formData?.assets?.length > formData?.expected_asset_count) errors.assets = 'Total Assets should not exceed Expected Asset Count';
    return errors;
  },
};

const PlannerCRU = ({ plan, lookups, onClose, onSaved, enqueueSnackbar }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [formData, setFormData] = useState(() => (plan ? { ...emptyPlan, ...plan } : emptyPlan));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const step = WIZARD_STEPS[stepIndex];

  const onFieldChange = (key, value) => {
    if (key === 'lessor') {
      const previousLessorId = formData?.lessor && formData?.lessor?.id;
      const nextLessorId = value && value?.id;
      if (previousLessorId !== nextLessorId && formData.assets.length) {
        enqueueSnackbar('Lessor changed — previously selected assets were cleared.', { variant: 'info' });
        setFormData((prev) => ({ ...prev, lessor: value, assets: [] }));
        return;
      }
    }
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const onFieldFocus = (key) => setErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));

  const goToStep = (index) => { if (index < stepIndex) setStepIndex(index); };

  const validateCurrentStep = () => {
    const validator = STEP_VALIDATORS[step.key];
    const stepErrors = validator ? validator(formData) : {};
    setErrors(stepErrors);
    return Object.keys(stepErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    if (stepIndex < WIZARD_STEPS.length - 1) {
      setStepIndex(stepIndex + 1);
    } else {
      onAddEditProgramPlan();
    }
  };

  const onAddEditProgramPlan = () => {
    let validationInputs = {};
    validationInputs = {
      name: formData?.name?.trim() ? '' : 'Please enter Project Name',
      project_type: formData?.project_type ? '' : 'Please select Project Type',
      lessor: formData?.lessor ? '' : 'Please select Lessor',
      lessee: formData?.lessee ? '' : 'Please select Lessee',
      client: formData?.client ? '' : 'Please select Client',
      location: formData?.location?.trim() ? '' : 'Please select Location',
      planned_end_date: formData?.planned_end_date ? '' : 'Please select End Date',
    };
    if (Object.keys(validationInputs).every((key) => { return validationInputs[key] === ''; })) {
      setSaving(true);
      const payload = buildPlanPayload(formData);
      if (formData.slug) {
        globalPutService(updateProgramPlanApi(formData.slug), payload).then((response) => {
          if (checkApiStatus(response)) {
            setSaving(false);
            onSaved();
            enqueueSnackbar(response?.data?.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
            trackActivity('Project Management', {
              event_type: 'Update Program Plan Success',
              page_title: 'Planner Tracker Update Program Plan',
              plan_slug: formData.slug,
              request_body: payload,
              response_msg: response?.data?.message,
            });
          } else {
            setSaving(false);
            enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
            trackActivity('Project Management', {
              event_type: 'Update Program Plan Failed',
              page_title: 'Planner Tracker Update Program Plan',
              plan_slug: formData.slug,
              request_body: payload,
              error_source: 'Backend',
              error_message: response?.data?.message,
            });
          }
        });
      } else {
        globalPostService(createProgramPlanApi(), payload).then((response) => {
          if (checkApiStatus(response)) {
            setSaving(false);
            onSaved();
            enqueueSnackbar(response?.data?.message, { variant: 'success', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
            trackActivity('Project Management', {
              event_type: 'Add Program Plan Success',
              page_title: 'Planner Tracker Add Program Plan',
              request_body: payload,
              response_msg: response?.data?.message,
            });
          } else {
            setSaving(false);
            enqueueSnackbar(response?.data?.message, { variant: 'error', anchorOrigin: { horizontal: 'right', vertical: 'top' } });
            trackActivity('Project Management', {
              event_type: 'Add Program Plan Failed',
              page_title: 'Planner Tracker Add Program Plan',
              request_body: payload,
              error_source: 'Backend',
              error_message: response?.data?.message,
            });
          }
        });
      }
    } else {
      setErrors(validationInputs);
      trackActivity('Project Management', {
        event_type: 'Add/Update Program Plan Failed',
        page_title: 'Planner Tracker Add/Update Program Plan',
        plan_slug: formData.slug || '',
        error_source: 'Frontend',
        error: validationInputs,
      });
    }
  };

  const handleBack = () => setStepIndex(Math.max(0, stepIndex - 1));

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle><Typography variant="h6" style={{ fontWeight: 700 }}>{plan ? 'Edit Project Plan' : 'Create Project Plan'}</Typography></DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={stepIndex} alternativeLabel style={{ marginBottom: 24, padding: 0 }}>
          {WIZARD_STEPS.map((wizardStep, index) => (
            <Step key={wizardStep.key} completed={index < stepIndex}>
              <StepLabel onClick={() => goToStep(index)} style={{ cursor: index < stepIndex ? 'pointer' : 'default' }}>
                {wizardStep.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {step.key === 'basic' ? <BasicInfoStep formData={formData} errors={errors} onFieldChange={onFieldChange} onFieldFocus={onFieldFocus} lookups={lookups} /> : null}
        {step.key === 'assets' ? <AssetsStep formData={formData} errors={errors} onFieldChange={onFieldChange} enqueueSnackbar={enqueueSnackbar} /> : null}
        {step.key === 'timeline' ? <TimelineStep formData={formData} errors={errors} onFieldChange={onFieldChange} onFieldFocus={onFieldFocus} /> : null}
        {step.key === 'team' ? <TeamStep formData={formData} onFieldChange={onFieldChange} enqueueSnackbar={enqueueSnackbar} /> : null}
        {step.key === 'review' ? <TrackingStep formData={formData} onFieldChange={onFieldChange} enqueueSnackbar={enqueueSnackbar} /> : null}
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={onClose} style={{ marginRight: 'auto' }}>Cancel</Button>
        <Button disabled={stepIndex === 0} onClick={handleBack}>Back</Button>
        <Button variant="contained" color="primary" disabled={saving} onClick={handleNext}>
          {saving ? <CircularProgress size={20} /> : (step.nextLabel ? `Next: ${step.nextLabel}` : (plan ? 'Save Changes' : 'Create Plan'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlannerCRU;
